import { useState, useEffect, useRef } from 'react';
import type { PortalMensagem } from '../../types';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  UserCircleIcon,
  ClockIcon,
  CheckCircleIcon,
  InboxIcon
} from '@heroicons/react/24/outline';

const API_BASE = (import.meta as any).env?.VITE_API_URL || '';

interface PortalMensagensInternasProps {
  submissaoId: string;
  portalId: string;
  nomeExterno?: string;
  emailExterno?: string;
  protocolo?: string;
  onClose?: () => void;
  currentUser?: {
    id: string;
    nome: string;
  };
  /** Renderiza apenas o painel interno (sem overlay/modal), para embutir dentro de outra tela (ex: card). */
  embedded?: boolean;
  /** Chamado após carregar/marcar como lidas, com a quantidade de mensagens externas não lidas. */
  onAtualizou?: (naoLidas: number) => void;
}

const PortalMensagensInternas = ({
  submissaoId,
  portalId,
  nomeExterno,
  emailExterno,
  protocolo,
  onClose,
  currentUser,
  embedded = false,
  onAtualizou
}: PortalMensagensInternasProps) => {
  const [mensagens, setMensagens] = useState<PortalMensagem[]>([]);
  const [loading, setLoading] = useState(true);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mensagensRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    carregarMensagens(true);
    marcarComoLidas();

    // Polling: atualiza as mensagens periodicamente enquanto o chat está aberto
    const interval = setInterval(() => {
      carregarMensagens(false);
    }, 8000);

    return () => clearInterval(interval);
  }, [submissaoId]);

  useEffect(() => {
    if (mensagensRef.current) {
      mensagensRef.current.scrollTop = mensagensRef.current.scrollHeight;
    }
  }, [mensagens]);

  const carregarMensagens = async (mostrarLoading = true) => {
    try {
      if (mostrarLoading) setLoading(true);
      const response = await fetch(`${API_BASE}/api/portal-mensagens?submissaoId=${submissaoId}`);
      const data = await response.json();

      if (data.success) {
        const lista = data.mensagens || [];
        setMensagens(lista);
        if (onAtualizou) {
          const naoLidas = lista.filter(
            (m: PortalMensagem) => m.origem === 'externo' && !m.lida
          ).length;
          onAtualizou(naoLidas);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      setError('Erro ao carregar mensagens');
    } finally {
      if (mostrarLoading) setLoading(false);
    }
  };

  const marcarComoLidas = async () => {
    try {
      await fetch(`${API_BASE}/api/portal-mensagens/marcar-lidas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissaoId })
      });
      if (onAtualizou) onAtualizou(0);
    } catch (err) {
      console.error('Erro ao marcar como lidas:', err);
    }
  };

  const enviarMensagem = async () => {
    if (!novaMensagem.trim()) return;

    setEnviando(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/portal-mensagens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submissaoId,
          portalId,
          mensagem: novaMensagem.trim(),
          origem: 'interno',
          remetenteId: currentUser?.id,
          remetenteNome: currentUser?.nome || 'Equipe'
        })
      });

      const data = await response.json();

      if (data.success) {
        setNovaMensagem('');
        carregarMensagens();
      } else {
        setError(data.error || 'Erro ao enviar mensagem');
      }
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setError('Erro ao enviar mensagem');
    } finally {
      setEnviando(false);
    }
  };

  const formatarData = (dataStr: string) => {
    // Datas ISO gravadas sem fuso vem do servidor em UTC; sem o 'Z' o
    // navegador as lia como horario local e exibia 3h a mais.
    const bruto = (dataStr || '').trim();
    const temFuso = /(Z|[+-]\d{2}:?\d{2})$/.test(bruto.slice(10));
    const data = new Date(temFuso || !bruto.includes('T') ? bruto : bruto + 'Z');
    if (isNaN(data.getTime())) return dataStr;
    return data.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const conteudo = (
    <>
        {/* Header (oculto no modo embutido) */}
        {!embedded && (
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="flex items-center gap-3">
            <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />
            <div>
              <h3 className="text-lg font-semibold text-white">
                Chat - {nomeExterno || emailExterno || 'Usuário Externo'}
              </h3>
              <p className="text-sm text-white/80">
                {protocolo && `Protocolo: ${protocolo}`}
                {emailExterno && ` • ${emailExterno}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-white" />
          </button>
        </div>
        )}

        {/* Mensagens */}
        <div 
          ref={mensagensRef}
          className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : mensagens.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <InboxIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Nenhuma mensagem ainda</p>
              <p className="text-sm text-gray-400 mt-1">
                Envie uma mensagem para o usuário externo
              </p>
            </div>
          ) : (
            mensagens.map(msg => (
              <div 
                key={msg.id}
                className={`flex ${msg.origem === 'interno' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] rounded-lg p-3 ${
                    msg.origem === 'interno' 
                      ? 'bg-blue-600 text-white rounded-br-none' 
                      : 'bg-white rounded-bl-none shadow-sm border border-gray-200'
                  }`}
                >
                  {/* Nome do remetente */}
                  <div className={`text-xs font-medium mb-1 flex items-center gap-1 ${
                    msg.origem === 'interno' ? 'text-white/80' : 'text-blue-600'
                  }`}>
                    <UserCircleIcon className="h-3 w-3" />
                    {msg.origem === 'interno' 
                      ? (msg.remetenteNome || 'Você') 
                      : (nomeExterno || emailExterno || 'Usuário Externo')
                    }
                  </div>

                  {/* Mensagem */}
                  <p className="text-sm whitespace-pre-wrap">{msg.mensagem}</p>

                  {/* Data e status */}
                  <div className={`text-xs mt-2 flex items-center justify-between gap-2 ${
                    msg.origem === 'interno' ? 'text-white/60' : 'text-gray-400'
                  }`}>
                    <span className="flex items-center gap-1">
                      <ClockIcon className="h-3 w-3" />
                      {formatarData(msg.createdAt)}
                    </span>
                    {msg.origem === 'interno' && msg.lida && (
                      <span className="flex items-center gap-1">
                        <CheckCircleIcon className="h-3 w-3" />
                        Lida
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200 bg-white">
          {error && (
            <div className="mb-3 p-2 bg-red-50 text-red-600 text-sm rounded-lg">
              {error}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={novaMensagem}
              onChange={(e) => setNovaMensagem(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !enviando && enviarMensagem()}
              placeholder="Digite sua resposta..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={enviando}
            />
            <button
              onClick={enviarMensagem}
              disabled={enviando || !novaMensagem.trim()}
              className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {enviando ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <PaperAirplaneIcon className="h-5 w-5" />
                  Enviar
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            O usuário externo receberá esta mensagem na página de acompanhamento do portal.
          </p>
        </div>
    </>
  );

  // Modo embutido: apenas o painel, sem overlay/modal (encaixa numa seção do card)
  if (embedded) {
    return (
      <div className="flex flex-col h-[28rem] bg-white rounded-lg border border-gray-200 overflow-hidden">
        {conteudo}
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10050] p-4"
      onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); }}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {conteudo}
      </div>
    </div>
  );
};

export default PortalMensagensInternas;
